import { DISMISS, useNotification } from "collections";
import { TableBody } from "collections/Table";
import { HelpLink, Loader, PolyIcon, SubmitButton, ToolTip } from "elements";
import { PolyPlusInactiveModal } from "elements/PolyPlus/PolyPlus";
import { Form, Formik } from "formik";
import {
  ServiceNowHookPageFragment,
  TestServiceNowWebhookByIdMutationVariables,
  useServiceNowHooksForTenantQuery,
  useTestServiceNowWebhookByIdMutation
} from "generated/graphql";
import {
  generatePath,
  Link,
  Route,
  RouteComponentProps,
  Switch,
  useRouteMatch
} from "polyrouter";
import { useCallback } from "react";
import { Trans, useTranslation } from "utilities/i18n";
import { usePolyPlusEnabled } from "utilities/polyPlus";
import { Layout } from "views/Main/Layouts";
import { PageTitle } from "../PageTitle";
import { CreateServiceNowHook } from "./Create/CreateServiceNowHook";
import { DeleteTenantServiceNowWebhook } from "./DeleteTenantServiceNowWebhook";
import ServiceNowWebHookEditForm from "./Edit/ServiceNowWebHookEditForm";
import * as yup from "yup";
import { getNotificationToDisplay } from "./helper";
import "../NotificationCenter.scss";
import { DOCS } from "Constants";

export function useServiceNowNotificationsConfiguration(id: string) {
  const { polyPlusEnabled, fetching: polyPlusFetching } = usePolyPlusEnabled(
    id
  );
  const { fetching, ...rest } = useServiceNowHooksForTenantQuery({
    variables: {
      id
    },
    pause: !polyPlusEnabled ?? true
  });
  return {
    fetching: fetching || polyPlusFetching,
    ...rest
  };
}

/**
 * Provide a shared submit action handler for forms to test a webhook.
 * You can call this on its own, or provide it to a form's onSubmit to also track
 * the submission process.
 */
export const useTestFormBehavior = () => {
  const [addNotification] = useNotification();
  const [, test] = useTestServiceNowWebhookByIdMutation();
  return useCallback(
    async (params: TestServiceNowWebhookByIdMutationVariables) => {
      const { data, error } = await test(params, {
        requestPolicy: "network-only"
      });
      if (error) return; //Notification of an invocation error is handled by the exchange

      const statusCode = data?.ncTestServiceNowWebhook.statusCode;
      const statusMessage = data?.ncTestServiceNowWebhook.statusMessage;
      const messageBody = data?.ncTestServiceNowWebhook.body;

      const success =
        statusCode !== undefined &&
        statusCode !== null &&
        statusCode > 199 &&
        statusCode < 300;

      const type = success ? "success" : "error";
      addNotification({
        type,
        message: getNotificationToDisplay(statusMessage, messageBody),
        dismiss: success ? DISMISS.SLOW : DISMISS.USER_ONLY
      });
    },
    // omit t and addNotification from deps, otherwise causes useEffect to call infinitely with no usefulness
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [test]
  );
};

const TestFormSchema = yup.object({
  testActionType: yup
    .string()
    .oneOf(["DEVICE_CAN_BE_UPDATED"])
    .required()
});

export const TestFormLinkStyle: React.FC<{
  webHooksId: string;
}> = ({ webHooksId }) => {
  const onSubmit = useTestFormBehavior();
  return (
    <Formik
      name="TestWebhookForm"
      validationSchema={TestFormSchema}
      initialValues={{
        testActionType: "DEVICE_CAN_BE_UPDATED"
      }}
      onSubmit={() => onSubmit({ id: webHooksId })}
    >
      {({ isSubmitting }) => (
        <Form>
          <SubmitButton
            className="btn btn-link"
            isSubmitting={isSubmitting}
            labelKey="TEST_WEBHOOK"
            submittingLabelKey="TESTING"
          />
        </Form>
      )}
    </Formik>
  );
};

const WebHookTable: React.FC<{
  tenantId: string;
  basePath: string;
  handleScroll: () => void;
  hooks: ServiceNowHookPageFragment[];
}> = ({ tenantId, basePath, handleScroll, hooks }) => {
  const { t } = useTranslation();
  const match = useRouteMatch<{ webHooksId: string }>([
    `${basePath}/:webHooksId`,
    basePath
  ]);
  const root = generatePath(basePath, { id: tenantId });
  const noneConfigured = hooks.length !== 0;

  if (!noneConfigured) return null;
  return (
    <table className="table custom-table">
      <thead>
        <tr>
          <th className="table-header">{t("NAME")}</th>
          <th className="table-header">{t("URL")}</th>
          <th className="table-header">{t("USERNAME")}</th>
          <th className="table-header">{t("STATUS")}</th>
          <th className="table-header" />
          <th className="table-header" />
        </tr>
      </thead>
      <TableBody cols={100} loading={false} accommodateScrollbar={false}>
        {hooks.map(
          ({
            name,
            username,
            url,
            id,
            lastErrorMessage,
            lastErrorTime,
            succeedingSince
          }) => (
            <tr
              key={id}
              className={
                match?.params?.webHooksId === id
                  ? "checked"
                  : `${match?.params?.webHooksId} ${id}`
              }
            >
              <td>
                <Link onClick={handleScroll} to={`${root}/${id}/edit`}>
                  {name}
                </Link>
              </td>
              <td className="webhook-url-cell">
                <ToolTip
                  icon=""
                  iconPosition="right"
                  position="bottom"
                  iconTitle=""
                  hideTextOverflow={true}
                  iconReplacement={url}
                >
                  <span>{url}</span>
                </ToolTip>
              </td>
              <td>
                <span>{username}</span>
              </td>
              <td>
                <LastErrorIcon
                  lastErrorTime={lastErrorTime}
                  succeedingSince={succeedingSince}
                  lastErrorMessage={lastErrorMessage}
                />
              </td>
              <td>
                <TestFormLinkStyle webHooksId={id} />
              </td>
              <td>
                <Link
                  to={generatePath(`${root}/:webHooksId/delete`, {
                    webHooksId: id
                  })}
                  className="link-delete"
                >
                  {t("DELETE")}
                </Link>
              </td>
            </tr>
          )
        )}
      </TableBody>
    </table>
  );
};

interface LastErrorInterface {
  lastErrorTime?: string | null;
  succeedingSince?: string | null;
  lastErrorMessage?: string | null;
}

const LastErrorIcon: React.FC<LastErrorInterface> = ({
  lastErrorTime,
  succeedingSince,
  lastErrorMessage
}) => {
  // Per Dan
  // Success: succeeding_since != null && (last_error_time == null || succeeding_since > last_error_time)
  // Failure: last_error_time != null && (succeeding_since == null || last_error_time > succeeding_since)
  // Untried: succeeding_since == null && last_error_time == null

  let formattedSucceedingSince;
  let formattedLastErrorTime;
  let displayLastErrorTime;

  if (succeedingSince) {
    formattedSucceedingSince = new Date(succeedingSince);
  }

  if (lastErrorTime) {
    formattedLastErrorTime = new Date(lastErrorTime);
    displayLastErrorTime = new Date(
      Date.UTC(
        formattedLastErrorTime.getFullYear(),
        formattedLastErrorTime.getMonth(),
        formattedLastErrorTime.getDay(),
        formattedLastErrorTime.getHours(),
        formattedLastErrorTime.getMinutes(),
        formattedLastErrorTime.getSeconds()
      )
    ).toUTCString();
  }

  const success =
    formattedSucceedingSince != null &&
    (formattedLastErrorTime == null ||
      formattedSucceedingSince > formattedLastErrorTime);
  const isDisconnected =
    formattedLastErrorTime != null &&
    (formattedSucceedingSince == null ||
      formattedLastErrorTime > formattedSucceedingSince);
  const icon = success
    ? "connected" // Success
    : isDisconnected
    ? "disconnected" // Failure
    : "minus_circle"; // Untried

  const toolTipMessage = success ? (
    "Success"
  ) : isDisconnected ? (
    <div>
      <p>{displayLastErrorTime}</p>
      <p>{lastErrorMessage}</p>
    </div>
  ) : (
    "Untried"
  );

  return (
    <span>
      <ToolTip
        icon={icon}
        iconPosition="right"
        position="right"
        iconTitle="help"
        iconReplacement={<PolyIcon icon={icon} />}
      >
        {toolTipMessage}
      </ToolTip>
    </span>
  );
};

const ServiceNowNotificationsConfiguration: React.FC<Pick<
  RouteComponentProps<{
    id: string;
  }>,
  "match"
>> = ({
  match: {
    params: { id },
    path
  }
}) => {
  const { fetching, data } = useServiceNowNotificationsConfiguration(id);
  const { t } = useTranslation();
  const hooks = data?.tenant.serviceNowWebhooks ?? [];
  const { Content } = Layout;

  const handleScroll = () => {
    let el = document.getElementsByClassName("scrollable")[0];
    let el2 = document.getElementById("title");
    let dimensions = el2?.getBoundingClientRect();
    if (dimensions == null) {
      return;
    }

    // 50 refers to the top margin, it doesn't matter whether this value is
    // decreased since we are trying to go to the very top, as long as the margin is less than 50
    // this magic number is correct which currently the element has a top margin of 20
    el?.scrollTo({
      left: el.scrollTop,
      top: dimensions.top - 50,
      behavior: "smooth"
    });
  };

  return (
    <Content>
      <Content.ScrollingList>
        <div className="account-settings notification-center-content">
          <PageTitle
            titleKey="NOTIFICATION_POLY_TITLE"
            subTitleKey="NOTIFICATION_SERVICE_TITLE"
          />
          <Route
            nest
            path="/:webHooksId/delete"
            component={DeleteTenantServiceNowWebhook}
          />
          <div className="options-section">
            {/* https://react.i18next.com/latest/trans-component */}
            {/* Component idx 0 must be a p tag, idx 1 must be the a tag.*/}
            <Trans i18nKey="NOTIFICATION_SERVICENOW_HELP_INTERVAL">
              <p>
                Description: To learn more, click
                <HelpLink location={DOCS.NOTIFICATION_OVERVIEW}>here</HelpLink>
              </p>
            </Trans>
          </div>
          <Loader loading={fetching}>
            <Switch nest>
              <Route
                path="/:webHooksId/edit"
                render={props => (
                  <ServiceNowWebHookEditForm
                    webHooksId={props.match.params.webHooksId}
                    hooks={hooks}
                  />
                )}
              />
              <Route path="/create" component={CreateServiceNowHook} />
              <Route>
                <div className="options-section">
                  <div className="grouping table-actions mb-1">
                    <Link
                      nest
                      to="/create"
                      className="btn btn-primary"
                      data-testid="add-servicenow-hook"
                    >
                      {t("ADD_NEW")}
                    </Link>
                  </div>
                </div>
              </Route>
            </Switch>
            <Switch nest>
              <Route path="/create" exact={false} />
              <Route>
                <WebHookTable
                  tenantId={id}
                  basePath={path}
                  handleScroll={handleScroll}
                  hooks={hooks}
                />
              </Route>
            </Switch>
          </Loader>
        </div>
      </Content.ScrollingList>
      {!fetching && <PolyPlusInactiveModal tenantId={id} />}
    </Content>
  );
};

export default ServiceNowNotificationsConfiguration;
